<script setup lang="ts">
import { ref, computed, onMounted } from "vue";

import { CfgGroups } from "@/types/enums";
import type { ChangeCallbackParams } from "@/types/config";

import { configStore } from "@/stores/config";
import { addErrorBottom } from "@/stores/messages";

import UCToggle from "@/components/ui/UCToggle.vue";
import SettingsOptionButton from "@/components/settings/SettingsOptionButton.vue";

const config = configStore();

const activeLanguageOptionItem = ref({ label: '', value: '' });
const activeCountryOptionItem = ref({ label: '', value: '' });
const activeTimezoneOptionItem = ref({ label: '', value: '' });
const activeUnitSystemOptionItem = ref({ label: '', value: '' });
const languageCode = ref('');
const countryCode = ref('');
const timezoneCode = ref('');
const timeFormat = ref(false);
const unitSystem = ref('');
const settingsSection = ref();

config.$subscribe((mutation, state) => {
  setConfigValues();
});

const menuLanguageItems = computed(() => {
  if (!config.list.languages || config.list.languages.length < 1) {
    return [];
  }

  return config.list.languages.map(lang => ({
    value: lang.code,
    label: lang.name,
  }));
});

const menuCountryItems = computed(() => {
  if (!config.list.countries || config.list.countries.length < 1) {
    return [];
  }

  return config.list.countries.map(country => ({
    value: country.code,
    label: country.name_en,
  }));
});

const menuTimezoneItems = computed(() => {
  if (!config.list.tz || config.list.tz.length < 1) {
    return [];
  }

  return config.list.tz.map(timezone => ({
    value: timezone,
    label: timezone,
  }));
});

const menuUnitSystemItems = computed(() => {
  if (!config.list.unitSystems || Object.keys(config.list.unitSystems).length < 1) {
    return [];
  }

  return Object.keys(config.list.unitSystems).map(unit => ({
    value: unit,
    label: unit,
  }));
});



async function onItemChange(params: ChangeCallbackParams) {
  try {
    await config.update(params.group as string, params.name as string, params.value);
  } catch (e: any) {
    addErrorBottom(e, null, settingsSection.value);
  }
}

function setConfigValues() {
  languageCode.value = config.$state.config?.localization?.language_code || '';
  countryCode.value = config.$state.config?.localization?.country_code || '';
  timezoneCode.value = config.$state.config?.localization?.time_zone || '';
  unitSystem.value = config.$state.config?.localization?.measurement_unit || '';
  activeLanguageOptionItem.value = menuLanguageItems.value.find(item => item.value === languageCode.value) || { label: '', value: '' };
  activeCountryOptionItem.value = menuCountryItems.value.find(item => item.value === countryCode.value) || { label: '', value: '' };
  activeTimezoneOptionItem.value = menuTimezoneItems.value.find(item => item.value === timezoneCode.value) || { label: '', value: '' };
  activeUnitSystemOptionItem.value = menuUnitSystemItems.value.find(item => item.value === unitSystem.value) || { label: '', value: '' };
  timeFormat.value = config.$state.config?.localization?.time_format_24h || false;
};

async function languageChanged(item: any) {
  const params: ChangeCallbackParams = {
    group: CfgGroups.localization,
    name: "language_code",
    value: item.value
  }
  onItemChange(params);
}

async function countryChanged(item: any) {
  const params: ChangeCallbackParams = {
    group: CfgGroups.localization,
    name: "country_code",
    value: item.value
  }
  onItemChange(params);
}
async function timeFormatChanged() {
  const params: ChangeCallbackParams = {
    group: CfgGroups.localization,
    name: "time_format_24h",
    value: timeFormat.value
  }
  onItemChange(params);
}

async function timezoneChanged(item: any) {
  const params: ChangeCallbackParams = {
    group: CfgGroups.localization,
    name: "time_zone",
    value: item.value
  }
  onItemChange(params);
}

async function unitSystemChanged(item: any) {
  const params: ChangeCallbackParams = {
    group: CfgGroups.localization,
    name: "measurement_unit",
    value: item.value
  }
  onItemChange(params);
}

onMounted(() => {
  setConfigValues();
});

</script>
<template>
  <div class="page-settings-section page-settings-section--localization" ref="settingsSection">
    <h1 class="page-settings-section__title">
      {{ $t('settings.localization.name') }}
    </h1>
    <div class="page-settings-section__main">
      <SettingsOptionButton
        :select="true"
        :selectUpdate="languageChanged"
        :activeOptionItem="activeLanguageOptionItem"
        :selectItems="menuLanguageItems"
        :label="$t('settings.localization.language')"
        :selectSearchable="true"
        :selectSmallScreenPosition="'right'"
      />
      <SettingsOptionButton
        :select="true"
        :selectUpdate="countryChanged"
        :activeOptionItem="activeCountryOptionItem"
        :selectItems="menuCountryItems"
        :label="$t('settings.localization.country')"
        :selectSearchable="true"
        :selectSmallScreenPosition="'right'"
      />
      <SettingsOptionButton
        :select="true"
        :selectUpdate="timezoneChanged"
        :activeOptionItem="activeTimezoneOptionItem"
        :selectItems="menuTimezoneItems"
        :label="$t('settings.localization.timezone')"
        :selectSearchable="true"
        :selectSmallScreenPosition="'right'"
      />
      <UCToggle
        v-model="timeFormat"
        @change="timeFormatChanged"
        :label="$t('settings.localization.time_format')"
        :full-w="true"
        :settings="true"
      />
      <SettingsOptionButton
        :select="true"
        :selectUpdate="unitSystemChanged"
        :activeOptionItem="activeUnitSystemOptionItem"
        :selectItems="menuUnitSystemItems"
        :label="$t('settings.localization.unit_system')"
        :selectSmallScreenPosition="'right'"
      />
    </div>
  </div>
</template>